// OS-214: shared rules for which employees may appear in a *selection* widget (dropdown,
// lookup, select box, grid filter row) versus the admin "Medewerkers" list, which must keep
// showing everyone. Kept in one place so every call site agrees on the same definition.
//
// - accountEnabled is nullable: only populated once an employee has been synced from Azure AD.
//   An employee that was never synced (accountEnabled == null) is treated as active - only an
//   explicit `false` means "account disabled" / inactive.
// - Project leader / quality guard selectability is gated by O365 group membership
//   (SecurityConfig.ProjectCoordinatorGroup / QualityGuardOneSkinGroup), resolved server-side by
//   GetEmployeeSelectionGroupsQuery (one O365 call per group, cached - see employeesModule.ts's
//   `fetchRoleGroups` action) rather than a per-employee flag. The resulting AAD object id sets
//   are passed in as `roleGroups`.

export type EmployeeRole = 'active' | 'projectLeader' | 'qualityGuard';

export interface RoleGroups {
  // AAD object ids of employees allowed to be picked for that role.
  //
  // `null`/`undefined` means the role's SecurityConfig group id is unconfigured, or the O365
  // lookup failed - callers MUST treat that as "no restriction" (fall back to all active
  // employees), never as "nobody qualifies": silently emptying every project leader / quality
  // guard dropdown because of a missing config value or a transient Graph outage would be far
  // worse than showing too many names.
  projectLeaderIds?: string[] | null;
  qualityGuardIds?: string[] | null;
}

export function isActiveEmployee(employee: any): boolean {
  return !!employee && employee.accountEnabled !== false;
}

// The AAD object id for this employee. Employee objects coming from the Mongo-backed
// `employeesRepository` (GET /api/employees) expose it as `adUserId`, separate from their Mongo
// `id`. Employee objects coming from `getEmployeesFromO365()` / the `employees/items` store are
// raw O365 DirectoryObjects, which are already keyed by AAD id - i.e. their `id` IS the AAD id,
// and they carry no separate `adUserId` field.
function aadUserId(employee: any): string | undefined {
  return employee?.adUserId || employee?.id;
}

export function isSelectableAsProjectLeader(employee: any, projectLeaderIds?: string[] | null): boolean {
  if (!isActiveEmployee(employee)) {
    return false;
  }
  if (!projectLeaderIds) {
    return true; // unconfigured group or failed O365 call -> fall back to all active employees
  }
  const id = aadUserId(employee);
  return !!id && projectLeaderIds.includes(id);
}

export function isSelectableAsQualityGuard(employee: any, qualityGuardIds?: string[] | null): boolean {
  if (!isActiveEmployee(employee)) {
    return false;
  }
  if (!qualityGuardIds) {
    return true; // unconfigured group or failed O365 call -> fall back to all active employees
  }
  const id = aadUserId(employee);
  return !!id && qualityGuardIds.includes(id);
}

function matchesRole(employee: any, role: EmployeeRole, roleGroups?: RoleGroups): boolean {
  switch (role) {
    case 'projectLeader':
      return isSelectableAsProjectLeader(employee, roleGroups?.projectLeaderIds);
    case 'qualityGuard':
      return isSelectableAsQualityGuard(employee, roleGroups?.qualityGuardIds);
    default:
      return isActiveEmployee(employee);
  }
}

export function sortEmployeesByName<T extends { displayName?: string }>(employees: T[]): T[] {
  return [...(employees || [])].sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '', 'nl', { sensitivity: 'base' })
  );
}

/**
 * Builds the item list for an employee picker: employees matching `role`, alphabetically
 * sorted, plus - crucially - any employee(s) already referenced by `currentValue` even if they
 * would otherwise be filtered out (disabled, or not selectable for this role). This guarantees
 * an edit screen never silently blanks/loses an already-assigned-but-now-disabled/unselectable
 * employee.
 *
 * @param employees full employee list (e.g. from employeesRepository.get() or the employees store)
 * @param options.role which selectability rule to apply (default: just "active")
 * @param options.roleGroups AAD id sets for 'projectLeader'/'qualityGuard' (ignored for 'active'); see RoleGroups
 * @param options.currentValue the value(s) currently set on the field being edited (single id, array of ids, or null/undefined for a "create new" context)
 * @param options.idField which field on the employee identifies it for comparison against currentValue (default: 'id')
 */
export function selectableEmployees<T extends { displayName?: string; accountEnabled?: boolean | null }>(
  employees: T[],
  options: { role?: EmployeeRole; roleGroups?: RoleGroups; currentValue?: any; idField?: string } = {}
): T[] {
  const { role = 'active', roleGroups, currentValue, idField = 'id' } = options;
  const list = employees || [];

  const currentIds = new Set<any>(
    (Array.isArray(currentValue) ? currentValue : currentValue !== undefined && currentValue !== null ? [currentValue] : []).filter(
      (v) => v !== undefined && v !== null && v !== ''
    )
  );

  const filtered = list.filter((e: any) => matchesRole(e, role, roleGroups) || currentIds.has(e[idField]));

  return sortEmployeesByName(filtered);
}
